import { useIntersection } from '@mantine/hooks'
import type { CSSProperties, ReactNode, RefObject } from 'react'
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import IcBaselineArrowBackIos from '~icons/ic/baseline-arrow-back-ios'
import IcBaselineArrowForwardIos from '~icons/ic/baseline-arrow-forward-ios'
import { cn, cx } from 'src/utils/cx'

export const HorizontalScrollable = forwardRef<
  RefObject<HTMLDivElement | null>,
  {
    children: ReactNode
    className?: string
    style?: CSSProperties
    showScrollButton?: boolean
    /** Custom class for the edge fade gradient overlay (default: from-background) */
    fadeClassName?: string
  }
>(
  (
    { children, className, style, showScrollButton = true, fadeClassName },
    ref,
  ) => {
    const maskContainerRef = useRef<HTMLDivElement>(null)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null)
    const edgeFadeWidth = 120

    useImperativeHandle(ref, () => scrollContainerRef)

    useLayoutEffect(() => {
      const scrollContainer = scrollContainerRef.current
      if (!scrollContainer) return

      const maskSize = edgeFadeWidth

      const handleScroll = () => {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainer
        const left = scrollLeft
        const right = scrollWidth - clientWidth - scrollLeft

        const leftOpacity = left > maskSize ? 0 : 1 - left / maskSize
        const rightOpacity = right > maskSize ? 0 : 1 - right / maskSize

        const mask = `linear-gradient(to right, rgba(255, 255, 255, ${leftOpacity}) 0%, rgba(255, 255, 255, 1) ${maskSize}px, rgba(255, 255, 255, 1) calc(100% - ${maskSize}px), rgba(255, 255, 255, ${rightOpacity}) 100%)`

        if (scrollContainerRef.current) {
          const el = scrollContainerRef.current
          el.style.mask = mask
          el.style.setProperty('-webkit-mask', mask)
        }
      }

      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      })

      handleScroll()

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }, [])

    useLayoutEffect(() => {
      setRootEl(scrollContainerRef.current)
    }, [])

    const { ref: startSentinelRef, entry: startIntersection } = useIntersection(
      {
        root: rootEl,
      },
    )
    const scrolledFromStart = startIntersection
      ? !startIntersection.isIntersecting
      : false
    const { ref: endSentinelRef, entry: endIntersection } = useIntersection({
      root: rootEl,
    })
    const scrolledToEnd = Boolean(endIntersection?.isIntersecting)

    return (
      <div ref={maskContainerRef} className="relative">
        <div
          ref={scrollContainerRef}
          className={cx(
            'overflow-x-auto overflow-y-visible relative z-0 hide-scrollbar flex *:flex-none',
          )}
        >
          <div
            ref={startSentinelRef}
            className="size-px -mr-px pointer-events-none"
          />
          <div className={className} style={style}>
            {children}
          </div>
          <div
            ref={endSentinelRef}
            className="size-px -ml-px pointer-events-none"
          />
        </div>

        {showScrollButton && (
          <>
            <div
              className={cx(
                'transition-all absolute top-0 bottom-0 left-0 overflow-hidden z-10 flex items-center justify-start',
              )}
              style={{
                width: edgeFadeWidth,
                opacity: scrolledFromStart ? 1 : 0,
                pointerEvents: scrolledFromStart ? 'auto' : 'none',
              }}
            >
              <div
                className={cn(
                  'absolute inset-0 pointer-events-none bg-gradient-to-r to-transparent',
                  fadeClassName || 'from-background',
                )}
              />
              <button
                type="button"
                className="relative flex items-center h-full -ml-2 p-2 transition rounded-none bg-transparent"
                onClick={() => {
                  scrollContainerRef.current?.scrollBy({
                    left: -100,
                    behavior: 'smooth',
                  })
                }}
              >
                <IcBaselineArrowBackIos className="size-6" />
              </button>
            </div>

            <div
              className={cx(
                'transition-all absolute top-0 bottom-0 right-0 overflow-hidden z-10 flex items-center justify-end',
              )}
              style={{
                width: edgeFadeWidth,
                opacity: scrolledToEnd ? 0 : 1,
                pointerEvents: scrolledToEnd ? 'none' : 'auto',
              }}
            >
              <div
                className={cn(
                  'absolute inset-0 pointer-events-none bg-gradient-to-l to-transparent',
                  fadeClassName || 'from-background',
                )}
              />
              <button
                type="button"
                className="relative flex items-center h-full -mr-2 p-2 transition rounded-none bg-transparent"
                onClick={() => {
                  scrollContainerRef.current?.scrollBy({
                    left: 100,
                    behavior: 'smooth',
                  })
                }}
              >
                <IcBaselineArrowForwardIos className="size-6" />
              </button>
            </div>
          </>
        )}
      </div>
    )
  },
)

HorizontalScrollable.displayName = 'HorizontalScrollable'
